import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { 
  MapPinIcon,
  StarIcon,
  ClockIcon,
  MagnifyingGlassIcon,
  ArrowLeftIcon,
  XMarkIcon,
  HeartIcon,
  TagIcon,
  FunnelIcon
} from '@heroicons/react/24/outline'
import { HeartIcon as HeartSolidIcon, StarIcon as StarSolidIcon } from '@heroicons/react/24/solid'
import { supabase } from '../config/supabase'
import toast from 'react-hot-toast'
import CustomerLoader from '../components/customer/CustomerLoader'

const ACTION_GREEN = '#00C853'

const SearchResults = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const searchQuery = searchParams.get('q') || ''
  const [restaurants, setRestaurants] = useState([])
  const [loading, setLoading] = useState(true)
  const [favorites, setFavorites] = useState(new Set())
  const [selectedFilter, setSelectedFilter] = useState('all')

  const filterOptions = [
    { id: 'all', name: 'Filters', icon: FunnelIcon },
    { id: 'open', name: 'Open Late', icon: ClockIcon },
    { id: 'offers', name: '30% OFF & above', icon: TagIcon }
  ]

  useEffect(() => {
    if (searchQuery) {
      fetchRestaurantsWithDish()
    }
  }, [searchQuery])

  const fetchRestaurantsWithDish = async () => {
    try {
      setLoading(true)
      
      // Search for menu items matching the query
      const { data: menuItems, error: menuError } = await supabase
        .from('menu_items')
        .select(`
          id,
          name,
          description,
          price,
          image_url,
          restaurant_id,
          is_available
        `)
        .ilike('name', `%${searchQuery}%`)
        .eq('is_available', true)

      if (menuError) throw menuError

      // Get unique restaurant IDs
      const restaurantIds = [...new Set(menuItems?.map(item => item.restaurant_id) || [])]

      if (restaurantIds.length === 0) {
        setRestaurants([])
        setLoading(false)
        return
      }

      // Fetch restaurant details
      const { data: restaurantsData, error: restaurantsError } = await supabase
        .from('users')
        .select(`
          id,
          restaurant_name,
          restaurant_description,
          restaurant_address,
          restaurant_phone,
          cuisine_type,
          logo_url,
          banner_url,
          is_active,
          is_open
        `)
        .in('id', restaurantIds)
        .eq('role', 'restaurant_owner')
        .eq('is_active', true)

      if (restaurantsError) throw restaurantsError

      // Enrich restaurants with menu items and ratings
      const enrichedRestaurants = await Promise.all(
        (restaurantsData || []).map(async (restaurant) => {
          // Get matching menu items for this restaurant
          const restaurantMenuItems = menuItems.filter(item => item.restaurant_id === restaurant.id)

          // Get reviews for rating
          const { data: reviews } = await supabase
            .from('reviews')
            .select('overall_rating')
            .eq('restaurant_id', restaurant.id)

          const avgRating = reviews?.length > 0 
            ? reviews.reduce((sum, review) => sum + review.overall_rating, 0) / reviews.length
            : 4.2

          return {
            ...restaurant,
            name: restaurant.restaurant_name,
            address: restaurant.restaurant_address,
            rating: Number(avgRating.toFixed(1)),
            review_count: reviews?.length || 0,
            matchingDishes: restaurantMenuItems,
            cost_for_two: 300 // Default value
          }
        })
      )

      setRestaurants(enrichedRestaurants)
    } catch (error) {
      console.error('Error fetching search results:', error)
      toast.error('Failed to load search results')
    } finally {
      setLoading(false)
    }
  }

  const handleRestaurantClick = (restaurant) => {
    // Navigate to restaurant details page for dine-out (booking)
    navigate(`/restaurant/${restaurant.id}`)
  }

  const toggleFavorite = (restaurantId, e) => {
    e.stopPropagation()
    const newFavorites = new Set(favorites)
    if (newFavorites.has(restaurantId)) {
      newFavorites.delete(restaurantId)
      toast.success('Removed from favorites')
    } else {
      newFavorites.add(restaurantId)
      toast.success('Added to favorites! ❤️')
    }
    setFavorites(newFavorites)
  }

  const filteredRestaurants = restaurants.filter(restaurant => {
    if (selectedFilter === 'open') {
      return restaurant.is_open
    } else if (selectedFilter === 'offers') {
      return true // Show all for now, can add offer logic
    }
    return true
  })

  return (
    <div className="min-h-screen bg-white">
      {/* Header with Back Button and Search */}
      <div className="bg-white sticky top-0 z-50 px-4 py-3" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
        <div className="flex items-center gap-3 mb-3">
          {/* Back Button */}
          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={() => navigate(-1)}
            className="w-10 h-10 bg-gray-100 flex items-center justify-center rounded-full hover:bg-gray-200 transition-colors"
          >
            <ArrowLeftIcon className="w-5 h-5 text-gray-700" />
          </motion.button>
          
          {/* Search Bar */}
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              readOnly
              placeholder="Search for restaurants, dishes, cuisines etc."
              className="w-full pl-12 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-700"
            />
          </div>
        </div>
      </div>

      {/* Dish Name Header */}
      <div className="px-4 py-4">
        <h1 className="text-3xl font-bold text-gray-900 mb-1 capitalize">{searchQuery}</h1>
        <p className="text-sm text-gray-600 uppercase tracking-wide">— ALL RESTAURANTS AROUND YOU —</p>
      </div>

      {/* Filter Chips */}
      <div className="px-4 pb-4">
        <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide">
          {filterOptions.map((filter) => {
            const Icon = filter.icon
            return (
              <motion.button
                key={filter.id}
                whileTap={{ scale: 0.97 }}
                onClick={() => setSelectedFilter(filter.id)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full font-medium text-xs whitespace-nowrap transition-all border ${
                  selectedFilter === filter.id
                    ? 'bg-white text-gray-900 border-gray-900'
                    : 'bg-white text-gray-700 border-gray-300 hover:border-gray-400'
                }`}
              >
                <Icon className="w-4 h-4" />
                <span>{filter.name}</span>
              </motion.button>
            )
          })}
        </div>
      </div>

      {/* Restaurant Results */}
      <div className="px-4 pb-20">
        {loading ? (
          <div className="py-8">
            <CustomerLoader />
          </div>
        ) : filteredRestaurants.length === 0 ? (
          <div className="text-center py-16">
            <div className="text-6xl mb-4">🔍</div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">No restaurants found</h3>
            <p className="text-gray-600 text-sm">Try searching for something else</p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredRestaurants.map((restaurant, index) => (
              <motion.div
                key={restaurant.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                onClick={() => handleRestaurantClick(restaurant)}
                className="bg-white rounded-2xl overflow-hidden cursor-pointer"
                style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}
              >
                {/* Restaurant Image */}
                <div className="relative h-48 overflow-hidden bg-gradient-to-br from-orange-100 to-orange-200">
                  {restaurant.banner_url || restaurant.logo_url ? (
                    <img
                      src={restaurant.banner_url || restaurant.logo_url}
                      alt={restaurant.name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none'
                        e.target.parentElement.innerHTML = '<div class="w-full h-full flex items-center justify-center bg-gradient-to-br from-orange-100 to-orange-200"><span class="text-6xl">🍽️</span></div>'
                      }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <span className="text-6xl">🍽️</span>
                    </div>
                  )}
                  
                  {/* Offer Badge */}
                  <div className="absolute top-3 left-3">
                    <span 
                      className="px-3 py-1.5 rounded-md text-xs font-bold bg-orange-500 text-white inline-flex items-center gap-1"
                      style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}
                    >
                      <TagIcon className="w-3 h-3" />
                      Flat 30% OFF
                    </span>
                  </div>

                  {/* Favorite Button */}
                  <div className="absolute top-3 right-3">
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={(e) => toggleFavorite(restaurant.id, e)}
                      className="p-2 bg-white rounded-full"
                      style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}
                    >
                      {favorites.has(restaurant.id) ? (
                        <HeartSolidIcon className="w-5 h-5 text-red-500" />
                      ) : (
                        <HeartIcon className="w-5 h-5 text-gray-600" />
                      )}
                    </motion.button>
                  </div>
                </div>

                {/* Restaurant Info */}
                <div className="p-4">
                  {/* Restaurant Name & Rating */}
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <h3 className="text-lg font-bold text-gray-900 leading-tight flex-1">
                      {restaurant.name || 'Restaurant'}
                    </h3>
                    
                    {/* Rating Badge */}
                    <div 
                      className="flex items-center gap-1 px-2 py-1 rounded-lg flex-shrink-0"
                      style={{ backgroundColor: ACTION_GREEN }}
                    >
                      <StarSolidIcon className="w-4 h-4 text-white" />
                      <span className="text-white font-bold text-sm">{restaurant.rating}</span>
                    </div>
                  </div>

                  {/* Cuisine & Location */}
                  <div className="flex items-center gap-2 text-sm text-gray-600 mb-3">
                    <span className="font-medium capitalize">{restaurant.cuisine_type || 'Multi-cuisine'}</span>
                    <span className="w-1 h-1 rounded-full bg-gray-400"></span>
                    <span>₹{restaurant.cost_for_two} for two</span>
                  </div>

                  {/* Location */}
                  <div className="flex items-center gap-2 text-sm text-gray-600 mb-3">
                    <span>11.2 km</span>
                    <span>|</span>
                    <MapPinIcon className="w-4 h-4" />
                    <span className="truncate flex-1">{restaurant.address || 'Rajouri Garden, New Delhi'}</span>
                  </div>

                  {/* Description */}
                  <div className="flex items-start gap-2 mb-3">
                    <span className="text-lg">✨</span>
                    <p className="text-sm text-gray-600 line-clamp-2 leading-relaxed">
                      Enjoy the signature {searchQuery}, Chap Malai Tikka, and Bhartkal Chicken Biryani. Save room for Gulab Jamun!
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default SearchResults
